import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@azure/msal-react';
import { EventType, type AccountInfo, type EventMessage } from '@azure/msal-browser';
import { App } from './App';
import { msalInstance, ensureMsalInitialized } from './auth/index';
import { captureHubClientParam } from '@lib/client-preselect';
import './index.css';

// The Workflow Hub lands the bench on /?client=<plan>. Sign-in is a redirect that returns to
// /login/callback, not to that URL, so remember the parameter for this tab BEFORE anything can
// redirect (lib/client-preselect.ts; a hint for the interface only, never an authorization).
captureHubClientParam(window.location.search);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Re-pull fresh data when the bench user returns to the site/tab (the live queue + batch + review
      // move underneath them); combined with refetchOnMount:'always' on the data hooks, entering the
      // site or switching tabs immediately fetches current data rather than showing a stale cache.
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      gcTime: 60_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

// Initialize MSAL and process any pending auth redirect BEFORE rendering (the portal's D1/D8 fix):
// the account cache is populated before any component can fire a gated /api call, so getApiToken()
// never misses the signed-in account on the post-Entra return navigation.
ensureMsalInitialized()
  .then(() => msalInstance.handleRedirectPromise())
  .catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('MSAL init/redirect processing failed (login wall will catch unauthenticated state)', e);
  })
  .finally(() => {
    // Pin the active account so getApiToken() mints the bearer for the SIGNED-IN identity rather
    // than getAllAccounts()[0] (which can be a stale/foreign cached account).
    try {
      if (!msalInstance.getActiveAccount()) {
        const first = msalInstance.getAllAccounts()[0];
        if (first) msalInstance.setActiveAccount(first);
      }
      msalInstance.addEventCallback((event: EventMessage) => {
        if (
          (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
          event.payload &&
          'account' in event.payload &&
          event.payload.account
        ) {
          msalInstance.setActiveAccount(event.payload.account as AccountInfo);
        }
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('MSAL active-account wiring failed', e);
    }
    createRoot(rootEl).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </MsalProvider>
      </StrictMode>,
    );
  });
